import React, { Fragment } from 'react'
import { useCallback, useMemo } from 'sb-hooks'
import { checkIfNavItemActive } from 'common/helpers'

import { AccordionButton, AccordionItem, AccordionPanel, Href } from 'components/_new/controls'
import { Box } from 'components/_new/layout'
import Text from 'components/_new/typography/Text/Text'
import { IconName } from 'components/_new/ui/Icon/Icon'

import NavListView from '../NavListView/NavListView'
import ItemView from './ItemView/ItemView'

import s from './NavListItem.scss'
import messages from './messages'


export type NavListItem = {
  id?: string
  title?: ReactIntlMessage | string
  to?: string
  href?: string
  toTab?: string
  iconName?: IconName
  emojiIcon?: string
  isSeparator?: boolean
  subNav?: NavListItem[]
  onClick?: React.MouseEventHandler
  dataTestId?: string
}

type NavListItemProps = {
  item: NavListItem,
  pathname?: string
  search?: string
  level?: number
  minSubNavLengthToDisplay?: number
  closeNav?: () => void
}

const NavListItem: React.FunctionComponent<NavListItemProps> = (props) => {
  const { item, pathname, search, level, minSubNavLengthToDisplay, closeNav } = props
  const { title, to, href, iconName, emojiIcon, toTab, dataTestId, id, isSeparator, subNav, onClick } = item

  const isActive = useMemo(() => (
    checkIfNavItemActive({ to, subNav, pathname, search })
  ), [ to, subNav, pathname, search ])

  const handleClick = useCallback((event) => {
    if (typeof onClick === 'function') {
      onClick(event)
    }

    if (typeof closeNav === 'function') {
      closeNav()
    }
  }, [ onClick, closeNav ])

  if (isSeparator) {
    return title ? (
      <Box pt={24} pb={8} pl={16}>
        <Text message={title} size="12-24" semibold uppercase color="gray" />
      </Box>
    ) : (
      <Box className={s.line} pt={8} pb={8} pl={16} />
    )
  }

  if (subNav && subNav.length >= minSubNavLengthToDisplay) {
    return (
      <AccordionItem>
        {
          ({ isOpen }) => (
            <Fragment>
              <AccordionButton className={s.button} dataTestId={dataTestId}>
                <ItemView
                  message={title}
                  iconName={iconName}
                  emojiIcon={emojiIcon}
                  isActive={isActive}
                  isOpen={isOpen}
                  level={level}
                  hasSubNav
                />
              </AccordionButton>
              <AccordionPanel>
                {
                  Boolean(to || toTab || href) && (
                    <Href
                      id={id}
                      className={s.link}
                      to={to}
                      href={href}
                      toTab={toTab}
                      onClick={handleClick}
                      dataTestId={`seeAll${dataTestId}`}
                    >
                      <ItemView
                        message={messages.seeAll}
                        isActive={to && to === pathname}
                        level={level + 1}
                      />
                    </Href>
                  )
                }
                <NavListView
                  items={subNav}
                  pathname={pathname}
                  level={level + 1}
                  minSubNavLengthToDisplay={minSubNavLengthToDisplay}
                  closeNav={closeNav}
                />
              </AccordionPanel>
            </Fragment>
          )
        }
      </AccordionItem>
    )
  }

  return to || toTab || href ? (
    <Href
      id={id}
      className={s.link}
      to={to}
      toTab={toTab}
      href={href}
      onClick={handleClick}
      data-testid={dataTestId}
    >
      <ItemView
        message={title}
        iconName={iconName}
        emojiIcon={emojiIcon}
        isActive={isActive}
        level={level}
      />
    </Href>
  ) : (
    <button
      id={id}
      className={s.button}
      type="button"
      onClick={handleClick}
      data-testid={dataTestId}
    >
      <ItemView
        message={title}
        iconName={iconName}
        emojiIcon={emojiIcon}
        isActive={isActive}
        level={level}
      />
    </button>
  )
}

export default NavListItem
